import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';

import { selectTabsConfig } from '@lib/core/products/selectors/resultCatalog';
import { TProductInstance } from '@lib/core/products/types';
import { getProductsListData } from '@lib/core/products/utils';
import { ApiQueries, GET_PRODUCTS_REQUEST_LIMIT } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { ILocaleText } from '@lib/tools/locale/views/LocaleFragment';

export type TTabConfig = {
  tabCode: string;
  tabText: ILocaleText;
  tabType: 'products' | 'custom';
  queryParams?: Record<string, string>;
  offset?: number;
  hasMore?: boolean;
  products?: TProductInstance[] | null;
  idQueueContext?: {
    queue: string[];
    index: number;
  };
};

interface IState {
  tabsConfig: TTabConfig[];
  activeTab: string;
  isLoading: boolean;
  error: SerializedError;
}

const initialState: IState = {
  activeTab: '',
  error: null,
  isLoading: false,
  tabsConfig: [],
};

export const actionProductsRequest = createTypedAsyncThunk<
  {
    tabCode: string;
    products: TProductInstance[];
    hasMore: boolean;
    offset: number;
    index?: number;
  },
  { tabCode: string; filterQueryParams?: Record<string, string>; limit?: number }
>('actionProductsRequest', async ({ tabCode, filterQueryParams, limit }, { getState }) => {
  try {
    const state = getState();
    const tabsConfig = selectTabsConfig(state);
    const tab = tabsConfig.find(t => t.tabCode === tabCode);
    if (!tab) throw new Error(`Tab not found: ${tabCode}`);

    const MAX_TOTAL = limit || 20;

    const products: TProductInstance[] = [];
    const queryParams = { ...tab.queryParams, ...filterQueryParams };
    const isMultipleRequest = tab.idQueueContext && tab.idQueueContext.queue?.length;

    if (isMultipleRequest) {
      const { queue = [], index: startIndex = 0 } = tab.idQueueContext;
      const startOffset = tab.offset ?? 0;

      const fetchProductsFromQueue = async (
        currentIndex: number,
        currentOffset: number,
      ): Promise<{ items: TProductInstance[]; index: number; offset: number }> => {
        const isReachedEndOfQueue = currentIndex >= queue.length;
        const isReachedMaxTotal = products.length >= MAX_TOTAL;

        if (isReachedEndOfQueue || isReachedMaxTotal) {
          return {
            index: currentIndex,
            items: products,
            offset: currentOffset,
          };
        }

        const characterId = queue[currentIndex];
        const response = await getProductsListData({
          ...queryParams,
          [ApiQueries.character]: characterId,
          limit: limit || GET_PRODUCTS_REQUEST_LIMIT,
          offset: currentOffset,
        });

        const fetchedProducts = response.results || [];
        const totalProductsForCharacter = response.count ?? 0;

        const remainingToFillLimit = MAX_TOTAL - products.length;
        const productsToAdd = fetchedProducts.slice(0, remainingToFillLimit);
        products.push(...productsToAdd);

        const updatedOffset = currentOffset + productsToAdd.length;
        const hasNoMoreProductsForCharacter =
          updatedOffset >= totalProductsForCharacter || fetchedProducts.length === 0;
        const needsMoreProducts = products.length < MAX_TOTAL;

        if (hasNoMoreProductsForCharacter && needsMoreProducts) {
          return await fetchProductsFromQueue(currentIndex + 1, 0);
        }

        return {
          index: hasNoMoreProductsForCharacter ? currentIndex + 1 : currentIndex,
          items: products,
          offset: hasNoMoreProductsForCharacter ? 0 : updatedOffset,
        };
      };

      const result = await fetchProductsFromQueue(startIndex, startOffset);

      return {
        hasMore: result.index < queue.length || result.offset > 0,
        index: result.index,
        offset: result.offset,
        products: result.items,
        tabCode,
      };
    }

    const response = await getProductsListData({
      ...queryParams,
      limit: limit || GET_PRODUCTS_REQUEST_LIMIT,
      offset: tab.offset ?? 0,
    });

    const fetched = response.results || [];
    const nextOffset = (tab.offset ?? 0) + fetched.length;

    return {
      hasMore: fetched.length === MAX_TOTAL,
      offset: nextOffset,
      products: fetched,
      tabCode,
    };
  } catch (e) {
    return Promise.reject(parseError(e));
  }
});

export const resultCatalogSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(actionProductsRequest.pending, state => {
        state.isLoading = true;
      })
      .addCase(actionProductsRequest.fulfilled, (state, action) => {
        const { tabCode, offset, hasMore, products } = action.payload;

        const tab = state.tabsConfig.find(t => t.tabCode === tabCode);
        if (!tab) return;

        tab.products = [...(tab.products ?? []), ...products];
        tab.offset = offset;
        tab.hasMore = hasMore;
        if (tab.idQueueContext && typeof action.payload.index === 'number') {
          tab.idQueueContext.index = action.payload.index;
        }
        state.isLoading = false;
      })
      .addCase(actionProductsRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      });
  },
  initialState,
  name: 'resultCatalog',
  reducers: {
    initTabsConfig(state, action: PayloadAction<TTabConfig[]>) {
      const currentActiveTab = state.activeTab;
      const newTabs = action.payload;

      const activeTabStillExists = newTabs.some(tab => tab.tabCode === currentActiveTab);

      state.tabsConfig = newTabs;
      state.activeTab = activeTabStillExists ? currentActiveTab : newTabs[0]?.tabCode || '';
    },
    resetResultCatalog: () => initialState,
    setActiveTab(state, action: PayloadAction<string>) {
      state.activeTab = action.payload;
    },
  },
});

export const { initTabsConfig, resetResultCatalog, setActiveTab } = resultCatalogSlice.actions;
export default resultCatalogSlice.reducer;
